import { create } from 'zustand';
import {
  PositionCategoryCreateModel,
  PositionCategoryFilter,
  PositionCategoryModel,
  PositionCategoryUpdateModel,
} from '../model/position.category.model';
import axiosInstance from '../../../configs/axios';
import { PositionCategoryChildrenModel } from '../data/position.category.child';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';
import { ApiPath } from '../../../constants/apiPath';
export interface PositionCategoryControllerState {
  listCate: PositionCategoryModel[] | [];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: PositionCategoryFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: PositionCategoryChildrenModel | null;
  formCreate: PositionCategoryCreateModel | null;
  formUpdate: PositionCategoryUpdateModel | null;
}

export interface PositionCategoryControllerAction {
  cateGetData: (startLoading?: boolean, filter?: PositionCategoryFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: PositionCategoryFilter | null) => void;
  onHandleModal: (activeModal?: PositionCategoryChildrenModel | null) => void;
  onUpdateFormCreate: (form: PositionCategoryCreateModel | null) => void;
  onUpdateFormUpdate: (form: PositionCategoryUpdateModel | null) => void;
  onSubmitForm: (form: PositionCategoryCreateModel | null) => void;
  onSubmitFormUpdate: (form: PositionCategoryUpdateModel | null) => void;
  onDelete: (id: string) => void;
}
export const PositionCategoryController = create<
  PositionCategoryControllerState & PositionCategoryControllerAction
>()((set) => ({
  loading: false,
  listCate: [],
  message: null,
  listCount: 0,
  page: 0,
  pageRowAmount: 9,
  filter: { skip: 0, take: 9 },
  activeModal: null,
  formCreate: null,
  formUpdate: null,
  cateGetData: async (startLoading?: boolean, filter?: PositionCategoryFilter | null) => {
    try {
      if (startLoading) set(() => ({ loading: true }));

      const res = await axiosInstance.get(ApiPath.positionCategory.all, {
        params: filter ?? { skip: 0, take: 9 },
      });
      const filteredData: any = res?.data?.data ?? [];
      const filteredDataTotal: any = res?.data?.count ?? 0;
      set(() => ({ listCate: filteredData, listCount: filteredDataTotal }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
  onPageChange: (page: number, rows: number) => {
    set(() => ({ pageRowAmount: rows, page: page }));
  },
  onFilterChange(filter?: PositionCategoryFilter | null) {
    set(() => ({ filter: filter }));
    this.cateGetData(true, filter);
  },
  onHandleModal: (activeModal?: PositionCategoryChildrenModel | null) => {
    set(() => ({ activeModal: activeModal, message: null }));
    if (activeModal === null) {
      set(() => ({ formCreate: null }));
    }
  },
  onUpdateFormCreate: (form: PositionCategoryCreateModel | null) => {
    set(() => ({ formCreate: form, message: null }));
  },
  onUpdateFormUpdate: (form: PositionCategoryUpdateModel | null) => {
    set(() => ({ formUpdate: form, message: null }));
  },
  async onSubmitForm(form: PositionCategoryCreateModel | null) {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }

    if (!form?.position_category_name_en || form?.position_category_name_en?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'en name required!' }));
    } else if (!form?.position_category_name_la || form?.position_category_name_la?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'la name required!' }));
    } else {
      try {
        set(() => ({ formCreate: form, loading: true }));
        const res = await axiosInstance.post(ApiPath.positionCategory.create, form);
        if (res?.status === 200) {
          set(() => ({ message: null, formCreate: null, filter: null, page: 0 }));
          this.cateGetData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  async onSubmitFormUpdate(form: PositionCategoryUpdateModel | null) {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }

    if (!form?.position_category_name_en || form?.position_category_name_en?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'en name required!' }));
    }
    if (!form?.position_category_name_la || form?.position_category_name_la?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'la name required!' }));
    } else {
      try {
        set(() => ({ formCreate: form, loading: true }));

        const res = await axiosInstance.put(ApiPath.positionCategory.update(form.id!), form);
        if (res?.status === 200) {
          set(() => ({ message: null, formUpdate: null, filter: null, page: 0 }));
          this.cateGetData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  async onDelete(id: string) {
    try {
      set(() => ({ loading: true }));
      const res = await axiosInstance.delete(ApiPath.positionCategory.delete(id));

      if (res?.status === 200) {
        this.cateGetData(true, null);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
}));

export default PositionCategoryController;
